import { useEffect, useState, type FormEvent } from 'react';
import { apiErrorText } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import { parseMoney } from '@shared/lib/money';
import {
  Form,
  Row,
  FormGroup,
  Label,
  Input,
  Select,
  ModalFormFooter,
  FieldError,
  FieldHint,
  HintButton,
  FormError,
} from './styled';
import { moscowTodayIso, taxPeriodSuggestions, taxTitle } from './taxPeriods';

interface CreateTaxModalProps {
  open: boolean;
  onClose: () => void;
  /** Сохраняет запись; при отказе сервера бросает ошибку — окно покажет её и сохранит ввод. */
  onCreateTax: (data: {
    taxPeriod: string;
    taxDueDate: string;
    taxType: string;
    taxTitle: string;
    taxAmount: number;
    taxReserved?: number;
  }) => Promise<void>;
  isCreating: boolean;
}

interface FieldErrors {
  amount?: string;
  reserved?: string;
}

export function CreateTaxModal({
  open,
  onClose,
  onCreateTax,
  isCreating,
}: CreateTaxModalProps) {
  // Период и срок не подставляются: обязательство заводят под конкретный период
  const [taxPeriod, setTaxPeriod] = useState('');
  const [taxDueDate, setTaxDueDate] = useState('');
  const [taxType, setTaxType] = useState('');
  // Пока название не правили руками, оно собирается из вида налога и периода
  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const [taxAmount, setTaxAmount] = useState('');
  const [taxReserved, setTaxReserved] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);

  const autoTitle = taxTitle(taxType, taxPeriod);
  const title = titleOverride ?? autoTitle;
  // Подсказки налоговых периодов — от московской даты, как текущий квартал на экране
  const periodOptions = taxPeriodSuggestions(moscowTodayIso());

  // Окно открыли заново — прежняя ошибка уже не про этот ввод
  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isCreating) return;
    const amount = parseMoney(taxAmount);
    const reserved = taxReserved.trim() ? parseMoney(taxReserved) : undefined;
    const errors: FieldErrors = {};
    if (amount === null) {
      errors.amount = 'Введите сумму начисления, например 1 500 000,00';
    } else if (amount <= 0) {
      errors.amount = 'Сумма начисления должна быть больше нуля';
    }
    if (reserved === null) {
      errors.reserved = 'Введите сумму резерва числом или оставьте поле пустым';
    }
    setFieldErrors(errors);
    if (amount === null || errors.amount || errors.reserved) return;
    if (!title.trim()) {
      setError('Укажите наименование обязательства');
      return;
    }

    setError(null);
    try {
      await onCreateTax({
        taxPeriod: taxPeriod.trim(),
        taxDueDate,
        taxType,
        taxTitle: title.trim(),
        taxAmount: amount,
        taxReserved: reserved ?? undefined,
      });
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось сохранить налоговое обязательство'));
      return;
    }
    // Сохранено — очищаем форму для следующей записи
    setTaxPeriod('');
    setTaxDueDate('');
    setTaxType('');
    setTitleOverride(null);
    setTaxAmount('');
    setTaxReserved('');
    setFieldErrors({});
  };

  return (
    <Modal
      open={open}
      title="Новое налоговое обязательство ЕНС"
      onClose={onClose}
    >
      <Form onSubmit={handleSubmit}>
        {error && <FormError role="alert">{error}</FormError>}

        <FormGroup>
          <Label htmlFor="tax-type">Вид налога *</Label>
          <Select
            id="tax-type"
            value={taxType}
            onChange={(e) => setTaxType(e.target.value)}
            required
          >
            <option value="" disabled>
              — выберите вид налога —
            </option>
            <option value="vat_1_3">1/3 НДС квартала</option>
            <option value="ndfl">НДФЛ с выплат работникам</option>
            <option value="insurance_contrib">Страховые взносы</option>
            <option value="profit_tax">Налог на прибыль</option>
            <option value="property_tax">Налог на имущество</option>
            <option value="ens_replenishment">Пополнение сальдо ЕНС</option>
          </Select>
        </FormGroup>

        <Row>
          <FormGroup>
            <Label htmlFor="tax-period">Период *</Label>
            <Input
              id="tax-period"
              list="tax-period-options"
              placeholder="Например: III кв. 2026"
              value={taxPeriod}
              onChange={(e) => setTaxPeriod(e.target.value)}
              required
            />
            <datalist id="tax-period-options">
              {periodOptions.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="tax-due-date">Срок уплаты *</Label>
            <Input
              id="tax-due-date"
              type="date"
              value={taxDueDate}
              onChange={(e) => setTaxDueDate(e.target.value)}
              required
            />
          </FormGroup>
        </Row>

        <FormGroup>
          <Label htmlFor="tax-title">Наименование обязательства *</Label>
          <Input
            id="tax-title"
            placeholder="Соберётся из вида налога и периода"
            value={title}
            onChange={(e) => setTitleOverride(e.target.value)}
            required
          />
          {titleOverride !== null && autoTitle && titleOverride.trim() !== autoTitle && (
            <FieldHint>
              Название изменено вручную — проверьте, что оно про выбранный налог и период.
              <HintButton type="button" onClick={() => setTitleOverride(null)}>
                Подставить «{autoTitle}»
              </HintButton>
            </FieldHint>
          )}
        </FormGroup>

        <Row>
          <FormGroup>
            <Label htmlFor="tax-amount">Сумма начисления, ₽ *</Label>
            <Input
              id="tax-amount"
              type="text"
              inputMode="decimal"
              placeholder="1 500 000,00"
              value={taxAmount}
              onChange={(e) => setTaxAmount(e.target.value)}
              aria-invalid={Boolean(fieldErrors.amount)}
              aria-describedby={fieldErrors.amount ? 'tax-amount-error' : undefined}
              required
            />
            {fieldErrors.amount && (
              <FieldError id="tax-amount-error">{fieldErrors.amount}</FieldError>
            )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="tax-reserved">Зарезервировано, ₽</Label>
            <Input
              id="tax-reserved"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={taxReserved}
              onChange={(e) => setTaxReserved(e.target.value)}
              aria-invalid={Boolean(fieldErrors.reserved)}
              aria-describedby={fieldErrors.reserved ? 'tax-reserved-error' : undefined}
            />
            {fieldErrors.reserved && (
              <FieldError id="tax-reserved-error">{fieldErrors.reserved}</FieldError>
            )}
          </FormGroup>
        </Row>

        <ModalFormFooter>
          <Button $variant="secondary" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button $variant="primary" type="submit" disabled={isCreating}>
            {isCreating ? 'Сохранение…' : 'Добавить'}
          </Button>
        </ModalFormFooter>
      </Form>
    </Modal>
  );
}
